import { useMemo } from "react";
import { Route, Routes } from "react-router";

import HideOnScrollProvider from "components/HideOnScroll/HideOnScrollContext";
import { AnalyticsPage } from "hooks/useAnalytics";
import useTitle from "hooks/useTitle";
import { EntityChangeType, Module, Run, RunType, Stack } from "types/generated";
import { isModuleRunType } from "utils/run";
import { isAnsibleStackVendor } from "utils/stack";
import useRunObservabilityEnabled from "shared/Run/useRunObservabilityEnabled";

import { RunContext } from "./Context";
import RunHeader from "./elements/Header/RunHeader";
import { ansibleRunHasTasks, runHasChanges } from "./helpers";
import RunChanges from "./elements/RunChanges";
import Changes from "./elements/RunChangesNext/Changes";
import AnsibleRunTasks from "./elements/AnsibleRunTasks";
import RunChangesLayout from "./elements/RunChangesLayout";
import RunObservability from "./routes/Observability";
import RunHistory from "./routes/History";
import PluginOutputs from "./routes/PluginOutputs";

type RunProps = {
  run: Run;
  stack: Stack | Module;
  versionId?: string;
  analyticsPage?: AnalyticsPage;
  configurationManagementViewEnabled?: boolean;
  hasAnsibleDetails?: boolean;
};

const RunNext = (props: RunProps) => {
  const {
    run,
    stack,
    versionId,
    analyticsPage,
    configurationManagementViewEnabled,
    hasAnsibleDetails,
  } = props;

  const isObservabilityEnabled = useRunObservabilityEnabled();

  const isTaskRun = run.type === RunType.Task;
  const isModuleRun = isModuleRunType(run.type);

  const isStackRun = !isTaskRun && !isModuleRun;
  const hasChanges = !!run.delta && runHasChanges(run.delta);
  const hasAnsibleTasks = !!run.delta && ansibleRunHasTasks(run.delta);
  const isStackRunWithChanges = isStackRun && hasChanges;

  const isAnsibleVendor =
    stack.__typename === "Stack" &&
    isAnsibleStackVendor(stack) &&
    configurationManagementViewEnabled;

  const runTitle = useMemo(() => {
    if (!(run && stack)) return "";

    if (isTaskRun) {
      return `${run.command || ""} · ${stack.name}`;
    }

    return `${run.title} · ${stack.name}`;
  }, [run, stack, isTaskRun]);

  useTitle(runTitle);

  return (
    <RunContext.Provider value={{ run, stack, versionId, hasAnsibleDetails }}>
      <HideOnScrollProvider>
        <RunHeader analyticsPage={analyticsPage} />
        <Routes>
          {!isAnsibleVendor && !isModuleRun && !isTaskRun && (
            <Route path="changes" element={<Changes />} />
          )}
          {isAnsibleVendor && hasAnsibleTasks && (
            <Route
              path="tasks"
              element={
                <AnsibleRunTasks analyticsPage={analyticsPage} stackId={stack.id} runId={run.id} />
              }
            />
          )}
          <Route path="plugin-outputs" element={<PluginOutputs />} />
          {isStackRun && isObservabilityEnabled && (
            <Route path="observability" element={<RunObservability />} />
          )}

          <Route index element={<RunHistory analyticsPage={analyticsPage} />} />

          {!isAnsibleVendor && isStackRunWithChanges && (
            <Route element={<RunChangesLayout />}>
              {!!run.delta?.addCount && (
                <Route path="changes/add" element={<RunChanges type={EntityChangeType.Add} />} />
              )}
              {!!run.delta?.changeCount && (
                <Route
                  path="changes/change"
                  element={<RunChanges type={EntityChangeType.Change} />}
                />
              )}
              {!!run.delta?.deleteCount && (
                <Route
                  path="changes/delete"
                  element={<RunChanges type={EntityChangeType.Delete} />}
                />
              )}
            </Route>
          )}
        </Routes>
      </HideOnScrollProvider>
    </RunContext.Provider>
  );
};

export default RunNext;
